import { i18n } from '@/shared/i18n';

/**
 * The picker's stored value is the English country name: it is persisted as
 * `persona.country` and passed verbatim into the persona-generation agent's input, so
 * existing records and prompts depend on it. Only the label is translated.
 */
export interface CountryOption {
  value: string;
  label: string;
}

/**
 * Each key is a persisted `persona.country` value and may not be reworded; only the label
 * behind its key may change. Declaration order is the order the picker shows.
 */
const LABEL_KEY_BY_COUNTRY = {
  'United States': 'common:utils.countries.unitedStates',
  Afghanistan: 'common:utils.countries.afghanistan',
  Albania: 'common:utils.countries.albania',
  Algeria: 'common:utils.countries.algeria',
  Andorra: 'common:utils.countries.andorra',
  Angola: 'common:utils.countries.angola',
  'Antigua and Barbuda': 'common:utils.countries.antiguaAndBarbuda',
  Argentina: 'common:utils.countries.argentina',
  Armenia: 'common:utils.countries.armenia',
  Australia: 'common:utils.countries.australia',
  Austria: 'common:utils.countries.austria',
  Azerbaijan: 'common:utils.countries.azerbaijan',
  Bahamas: 'common:utils.countries.bahamas',
  Bahrain: 'common:utils.countries.bahrain',
  Bangladesh: 'common:utils.countries.bangladesh',
  Barbados: 'common:utils.countries.barbados',
  Belarus: 'common:utils.countries.belarus',
  Belgium: 'common:utils.countries.belgium',
  Belize: 'common:utils.countries.belize',
  Benin: 'common:utils.countries.benin',
  Bhutan: 'common:utils.countries.bhutan',
  Bolivia: 'common:utils.countries.bolivia',
  'Bosnia and Herzegovina': 'common:utils.countries.bosniaAndHerzegovina',
  Botswana: 'common:utils.countries.botswana',
  Brazil: 'common:utils.countries.brazil',
  Brunei: 'common:utils.countries.brunei',
  Bulgaria: 'common:utils.countries.bulgaria',
  'Burkina Faso': 'common:utils.countries.burkinaFaso',
  Burundi: 'common:utils.countries.burundi',
  'Cabo Verde': 'common:utils.countries.caboVerde',
  Cambodia: 'common:utils.countries.cambodia',
  Cameroon: 'common:utils.countries.cameroon',
  Canada: 'common:utils.countries.canada',
  'Central African Republic': 'common:utils.countries.centralAfricanRepublic',
  Chad: 'common:utils.countries.chad',
  Chile: 'common:utils.countries.chile',
  China: 'common:utils.countries.china',
  Colombia: 'common:utils.countries.colombia',
  Comoros: 'common:utils.countries.comoros',
  Congo: 'common:utils.countries.congo',
  'Costa Rica': 'common:utils.countries.costaRica',
  Croatia: 'common:utils.countries.croatia',
  Cuba: 'common:utils.countries.cuba',
  Cyprus: 'common:utils.countries.cyprus',
  'Czech Republic': 'common:utils.countries.czechRepublic',
  Denmark: 'common:utils.countries.denmark',
  Djibouti: 'common:utils.countries.djibouti',
  Dominica: 'common:utils.countries.dominica',
  'Dominican Republic': 'common:utils.countries.dominicanRepublic',
  Ecuador: 'common:utils.countries.ecuador',
  Egypt: 'common:utils.countries.egypt',
  'El Salvador': 'common:utils.countries.elSalvador',
  'Equatorial Guinea': 'common:utils.countries.equatorialGuinea',
  Eritrea: 'common:utils.countries.eritrea',
  Estonia: 'common:utils.countries.estonia',
  Eswatini: 'common:utils.countries.eswatini',
  Ethiopia: 'common:utils.countries.ethiopia',
  Fiji: 'common:utils.countries.fiji',
  Finland: 'common:utils.countries.finland',
  France: 'common:utils.countries.france',
  Gabon: 'common:utils.countries.gabon',
  Gambia: 'common:utils.countries.gambia',
  Georgia: 'common:utils.countries.georgia',
  Germany: 'common:utils.countries.germany',
  Ghana: 'common:utils.countries.ghana',
  Greece: 'common:utils.countries.greece',
  Grenada: 'common:utils.countries.grenada',
  Guatemala: 'common:utils.countries.guatemala',
  Guinea: 'common:utils.countries.guinea',
  'Guinea-Bissau': 'common:utils.countries.guineaBissau',
  Guyana: 'common:utils.countries.guyana',
  Haiti: 'common:utils.countries.haiti',
  Honduras: 'common:utils.countries.honduras',
  Hungary: 'common:utils.countries.hungary',
  Iceland: 'common:utils.countries.iceland',
  India: 'common:utils.countries.india',
  Indonesia: 'common:utils.countries.indonesia',
  Iran: 'common:utils.countries.iran',
  Iraq: 'common:utils.countries.iraq',
  Ireland: 'common:utils.countries.ireland',
  Israel: 'common:utils.countries.israel',
  Italy: 'common:utils.countries.italy',
  Jamaica: 'common:utils.countries.jamaica',
  Japan: 'common:utils.countries.japan',
  Jordan: 'common:utils.countries.jordan',
  Kazakhstan: 'common:utils.countries.kazakhstan',
  Kenya: 'common:utils.countries.kenya',
  Kiribati: 'common:utils.countries.kiribati',
  Kosovo: 'common:utils.countries.kosovo',
  Kuwait: 'common:utils.countries.kuwait',
  Kyrgyzstan: 'common:utils.countries.kyrgyzstan',
  Laos: 'common:utils.countries.laos',
  Latvia: 'common:utils.countries.latvia',
  Lebanon: 'common:utils.countries.lebanon',
  Lesotho: 'common:utils.countries.lesotho',
  Liberia: 'common:utils.countries.liberia',
  Libya: 'common:utils.countries.libya',
  Liechtenstein: 'common:utils.countries.liechtenstein',
  Lithuania: 'common:utils.countries.lithuania',
  Luxembourg: 'common:utils.countries.luxembourg',
  Madagascar: 'common:utils.countries.madagascar',
  Malawi: 'common:utils.countries.malawi',
  Malaysia: 'common:utils.countries.malaysia',
  Maldives: 'common:utils.countries.maldives',
  Mali: 'common:utils.countries.mali',
  Malta: 'common:utils.countries.malta',
  'Marshall Islands': 'common:utils.countries.marshallIslands',
  Mauritania: 'common:utils.countries.mauritania',
  Mauritius: 'common:utils.countries.mauritius',
  Mexico: 'common:utils.countries.mexico',
  Micronesia: 'common:utils.countries.micronesia',
  Moldova: 'common:utils.countries.moldova',
  Monaco: 'common:utils.countries.monaco',
  Mongolia: 'common:utils.countries.mongolia',
  Montenegro: 'common:utils.countries.montenegro',
  Morocco: 'common:utils.countries.morocco',
  Mozambique: 'common:utils.countries.mozambique',
  Myanmar: 'common:utils.countries.myanmar',
  Namibia: 'common:utils.countries.namibia',
  Nauru: 'common:utils.countries.nauru',
  Nepal: 'common:utils.countries.nepal',
  Netherlands: 'common:utils.countries.netherlands',
  'New Zealand': 'common:utils.countries.newZealand',
  Nicaragua: 'common:utils.countries.nicaragua',
  Niger: 'common:utils.countries.niger',
  Nigeria: 'common:utils.countries.nigeria',
  'North Korea': 'common:utils.countries.northKorea',
  'North Macedonia': 'common:utils.countries.northMacedonia',
  Norway: 'common:utils.countries.norway',
  Oman: 'common:utils.countries.oman',
  Pakistan: 'common:utils.countries.pakistan',
  Palau: 'common:utils.countries.palau',
  Palestine: 'common:utils.countries.palestine',
  Panama: 'common:utils.countries.panama',
  'Papua New Guinea': 'common:utils.countries.papuaNewGuinea',
  Paraguay: 'common:utils.countries.paraguay',
  Peru: 'common:utils.countries.peru',
  Philippines: 'common:utils.countries.philippines',
  Poland: 'common:utils.countries.poland',
  Portugal: 'common:utils.countries.portugal',
  Qatar: 'common:utils.countries.qatar',
  Romania: 'common:utils.countries.romania',
  Russia: 'common:utils.countries.russia',
  Rwanda: 'common:utils.countries.rwanda',
  'Saint Kitts and Nevis': 'common:utils.countries.saintKittsAndNevis',
  'Saint Lucia': 'common:utils.countries.saintLucia',
  'Saint Vincent and the Grenadines': 'common:utils.countries.saintVincentAndTheGrenadines',
  Samoa: 'common:utils.countries.samoa',
  'San Marino': 'common:utils.countries.sanMarino',
  'Sao Tome and Principe': 'common:utils.countries.saoTomeAndPrincipe',
  'Saudi Arabia': 'common:utils.countries.saudiArabia',
  Senegal: 'common:utils.countries.senegal',
  Serbia: 'common:utils.countries.serbia',
  Seychelles: 'common:utils.countries.seychelles',
  'Sierra Leone': 'common:utils.countries.sierraLeone',
  Singapore: 'common:utils.countries.singapore',
  Slovakia: 'common:utils.countries.slovakia',
  Slovenia: 'common:utils.countries.slovenia',
  'Solomon Islands': 'common:utils.countries.solomonIslands',
  Somalia: 'common:utils.countries.somalia',
  'South Africa': 'common:utils.countries.southAfrica',
  'South Korea': 'common:utils.countries.southKorea',
  'South Sudan': 'common:utils.countries.southSudan',
  Spain: 'common:utils.countries.spain',
  'Sri Lanka': 'common:utils.countries.sriLanka',
  Sudan: 'common:utils.countries.sudan',
  Suriname: 'common:utils.countries.suriname',
  Sweden: 'common:utils.countries.sweden',
  Switzerland: 'common:utils.countries.switzerland',
  Syria: 'common:utils.countries.syria',
  Taiwan: 'common:utils.countries.taiwan',
  Tajikistan: 'common:utils.countries.tajikistan',
  Tanzania: 'common:utils.countries.tanzania',
  Thailand: 'common:utils.countries.thailand',
  'Timor-Leste': 'common:utils.countries.timorLeste',
  Togo: 'common:utils.countries.togo',
  Tonga: 'common:utils.countries.tonga',
  'Trinidad and Tobago': 'common:utils.countries.trinidadAndTobago',
  Tunisia: 'common:utils.countries.tunisia',
  Turkey: 'common:utils.countries.turkey',
  Turkmenistan: 'common:utils.countries.turkmenistan',
  Tuvalu: 'common:utils.countries.tuvalu',
  Uganda: 'common:utils.countries.uganda',
  Ukraine: 'common:utils.countries.ukraine',
  'United Arab Emirates': 'common:utils.countries.unitedArabEmirates',
  'United Kingdom': 'common:utils.countries.unitedKingdom',
  Uruguay: 'common:utils.countries.uruguay',
  Uzbekistan: 'common:utils.countries.uzbekistan',
  Vanuatu: 'common:utils.countries.vanuatu',
  'Vatican City': 'common:utils.countries.vaticanCity',
  Venezuela: 'common:utils.countries.venezuela',
  Vietnam: 'common:utils.countries.vietnam',
  Yemen: 'common:utils.countries.yemen',
  Zambia: 'common:utils.countries.zambia',
  Zimbabwe: 'common:utils.countries.zimbabwe',
} as const;

export function getCountryOptions(locale: string): CountryOption[] {
  return Object.entries(LABEL_KEY_BY_COUNTRY).map(([value, key]) => ({
    value,
    label: i18n.t(key, { lng: locale }),
  }));
}
